/**
 * Ensure that value is returned as array
 *
 * @example
 *   console.log(ensureArray('value')); // ['value']
 *   console.log(ensureArray(['value'])); // ['value']
 */
export function ensureArray(value) {
    return Array.isArray(value) ? value : [value];
}
/**
 * Converts array values to breakpoint object
 *
 * @example
 *   const values = [true, false, true];
 *   const breakpointValuesObject = arrayToBreakpointObject(values, [
 *     'base',
 *     'sm',
 *     'md',
 *     'lg',
 *   ]);
 *   console.log(breakpointValuesObject); // {base: true, sm: false, md: true}
 */
export function arrayToBreakpointObject(
/** The object with the values */
values, 
/** All available breakpoints */
breakpoints) {
    const result = {};
    values.forEach((value, index) => {
        const key = breakpoints[index];
        if (value == null)
            return;
        result[key] = value;
    });
    return result;
}
/**
 * Gets the closest breakpoint value available to the current breakpoint.
 *
 * @example
 *   const value = getClosestBreakpointValue({ sm: true, md: null }, 'md', [
 *     'sm',
 *     'md',
 *     'lg',
 *   ]);
 *
 *   console.log(value); // true
 */
export function getClosestBreakpointValue(
/** The object with the values */
values, 
/** The current breakpoint */
breakpoint, 
/** All available breakpoints */
breakpoints) {
    if (Object.prototype.hasOwnProperty.call(values, breakpoint) &&
        values[breakpoint] != null) {
        return values[breakpoint];
    }
    let index = -1;
    let lastIndex = breakpoints.indexOf(breakpoint);
    while (lastIndex >= 0) {
        const key = breakpoints[lastIndex];
        if (Object.prototype.hasOwnProperty.call(values, key) &&
            values[key] != null) {
            index = lastIndex;
            break;
        }
        lastIndex = lastIndex - 1;
    }
    if (index !== -1) {
        const key = breakpoints[index];
        return values[key];
    }
}
/**
 * Sorts the array based on the first number in the string of a and b
 *
 * @example
 *   const sorted = [
 *     ['md', '400px'],
 *     ['sm', '300px'],
 *   ].sort(sortByBreakpointValue);
 *   console.log(sorted); // [['sm', '300px'], ['md', '400px']]
 */
export function sortByBreakpointValue(a, b) {
    return parseInt(a[1]) > parseInt(b[1]) ? 1 : -1;
}
/**
 * Subtracts 1 pixel or equivalent in em to be used for max width query string
 *
 * @example
 *   const maxWidthQueryStringInEm = subtractForMediaQueryMaxWidth(30em);
 *   console.log('29.9375em')
 *
 *   const maxWidthQueryStringInPx = subtractForMediaQueryMaxWidth(30px);
 *   console.log('29px')
 *
 * @param minWidth Value in px / em
 */
export function subtractForMediaQueryMaxWidth(minWidth) {
    const num = parseFloat(minWidth);
    const unit = minWidth.replace(num.toString(), '');
    // 0.0625 is a px with em width a 16px base
    const subtractFactor = unit === 'px' ? -1 : -0.0625;
    return `${num + subtractFactor}${unit}`;
}
/** Returns media query strings without media type eg. @media screen... */
export function toMediaQueryString(
/** The minimum width */
min, 
/** The maximum height */
max) {
    const query = [];
    if (min)
        query.push(`(min-width: ${min})`);
    if (min && max) {
        query.push('and');
    }
    if (max)
        query.push(`(max-width: ${max})`);
    return query.join(' ');
}
/**
 * Gets the breakpoints keys in sorted array
 *
 * @example
 *   const keys = getSortedBreakpointsKeys({ md: '800px', sm: '400px' });
 *   console.log(keys); // ['sm', 'md']
 */
export function getSortedBreakpointsKeys(breakpoints) {
    return Object.keys(Object.fromEntries(Object.entries(breakpoints).sort(sortByBreakpointValue)));
}
